import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsItem from "@/components/base/NewsItem";
import Pagination from "@/components/base/Pagination";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function News() {
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return newsList;
    return newsList.filter((n) => n.title.toLowerCase().includes(kw));
  }, [keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <Layout>
      <section className="w-full px-4 md:px-8 pt-6 md:pt-10 pb-6 border-b border-background-200 bg-background-100">
        <Breadcrumb items={[{ label: "影视资讯" }]} />
        <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              影视资讯 - 爱爱网
            </h1>
            <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
              汇总最新影视动态、票房速递与行业观察，助你快速掌握影视圈新鲜事。
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-full px-4 h-11 bg-background-50 border border-background-300 w-full md:w-72">
            <i className="ri-search-line text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              placeholder="搜索资讯标题关键词"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword("")}
                className="w-6 h-6 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-200 cursor-pointer"
                aria-label="清除"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-4">
              <i className="ri-file-search-line text-3xl" />
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-800">
              没有找到匹配的资讯
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              换个关键词试试，或返回查看全部资讯
            </p>
            <Link
              to="/news"
              onClick={() => setKeyword("")}
              className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回资讯首页
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="rounded-lg border border-background-200 bg-background-50 px-5">
                {paged.map((news) => (
                  <NewsItem key={news.id} news={news} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>

            <aside>
              <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-4 rounded-full bg-accent-500" />
                  热门推荐
                </h2>
                <div className="flex flex-col gap-3">
                  {newsList.slice(0, 5).map((news, i) => (
                    <Link
                      key={news.id}
                      to={`/news/${news.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <span
                        className={`shrink-0 w-5 h-5 rounded text-[11px] font-bold flex items-center justify-center mt-0.5 ${
                          i < 3
                            ? "bg-accent-500 text-primary-950"
                            : "bg-background-200 text-foreground-500"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <p className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {news.title}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
    </Layout>
  );
}